import { useCallback, useMemo, useState } from 'react';
import { Grid2 as Grid, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { defaultsDeep } from 'lodash';
import { AnchorField, FormattedTextField, getAnchorRowStyle } from 'framework/ui/field';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  CUSTOMER_REPORT_STATUS,
  ModalAccessButton,
  ProjectSectionAccordion,
  SAVE_EXTERNAL_REPORT,
  useNewProjectUrl,
  useProjectDataContext,
  useProjectSecurityContext,
} from 'domain/shared/newProject';
import { PlusIcon } from 'framework/ui/mui-builder';

import StatusField from './StatusField';
import CustomReportLinkField from './CustomLinkField';
import { Modal } from 'framework/ui/ui-builder';
import { EDITABLE_LIST_CUSTOM, EDITABLE_LIST_FIELD, EditableListDataGridLayout, useListContext } from 'framework/list';
import ExternalReportModal from '../../createupdate/ExternalReportModal';
import ExistingReportModal from '../../createupdate/ExistingReportModal';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { type Data } from 'framework/types';
import UpdateExternalReportModal from '../../createupdate/UpdateExternalReportModal';
import { ShowData } from 'framework/show';
import moment from 'moment';
import { DATE_FORMAT, openPageInNewTab } from 'framework/utils';

const ReportList = () => {
  const { data: { items = [] } = {} } = useListContext();
  const { data: projectData } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const { getCreateCustomerReportUrl } = useNewProjectUrl();
  const [modalOpen, setModalOpen] = useState(false);
  const [existingReportModalOpen, setExistingReportModalOpen] = useState(false);
  const [externalModalOpen, setExternalModalOpen] = useState(false);
  const [updateExternalModalOpen, setUpdateExternalModalOpen] = useState(false);
  const [updateExternalModalData, setUpdateExternalModalData] = useState<Data>(new ShowData({}));

  const draftReport = useMemo(() => {
    return items?.find((report: any) => report.status === CUSTOMER_REPORT_STATUS.DRAFT);
  }, [items]);

  const inApprovalReport = useMemo(() => {
    return items?.find((report: any) => report.status === CUSTOMER_REPORT_STATUS.IN_APPROVAL);
  }, [items]);

  const rejectedReport = useMemo(() => {
    return items?.find((report: any) => report.status === CUSTOMER_REPORT_STATUS.REJECTED);
  }, [items]);

  const getCreateReportUrl = useCallback(() => {
    const isSystemReportUsed = projectData?.settings?.reportSettings?.isSystemReportUsed;

    if (isSystemReportUsed === false) {
      return setExternalModalOpen(true);
    } else {
      return openPageInNewTab(getCreateCustomerReportUrl(projectData.id!));
    }
  }, [projectData, getCreateCustomerReportUrl]);

  const handleCreateReport = useCallback(() => {
    if (draftReport) {
      setModalOpen(true);
    } else if (inApprovalReport || rejectedReport) {
      setExistingReportModalOpen(true);
    } else {
      getCreateReportUrl();
    }
  }, [draftReport, getCreateReportUrl, inApprovalReport, rejectedReport]);

  const renderActions = useCallback(() => {
    if (!isAccessGranted(ACCESS_INFO_SECTIONS.CUSTOMER_REPORT, ACCESS_INFO_ACTIONS.MANAGE)) return null;
    return [
      <IconButton sx={{ p: 0, ml: 1 }} key="add" onClick={handleCreateReport}>
        <PlusIcon />
      </IconButton>,
    ];
  }, [handleCreateReport, isAccessGranted]);

  return (
    <>
      <ProjectSectionAccordion
        defaultExpanded
        title={
          <Grid container direction="row" justifyContent="space-between" alignItems="center" width="100%" pl={2}>
            <Grid size={6}>
              <Typography variant="h4" color="primary">
                Customer report
              </Typography>
            </Grid>
            <Grid size="auto" alignItems="center" sx={{ '.filter-item-wrapper': { m: 0 } }} />
            <Grid size="grow" />
            <Grid size="auto" display="flex" alignItems="center">
              {renderActions()}
            </Grid>
          </Grid>
        }
      >
        <EditableListDataGridLayout
          rowProps={data =>
            defaultsDeep(getAnchorRowStyle(data, ''), {
              sx: {
                alignItems: 'center',
                height: '40px',
              },
            })
          }
          getTableCells={async isEditModeLayout => {
            return Promise.resolve([
              {
                type: EDITABLE_LIST_FIELD,
                source: 'index',
                isEditor: false,
                component: AnchorField,
                componentProps: {
                  label: '',
                  sx: {
                    fontWeight: 500,
                    color: 'text.secondary',
                  },
                },
                gridCellProps: { size: 0.5 },
              },

              {
                type: EDITABLE_LIST_CUSTOM,
                isEditor: false,
                component: CustomReportLinkField,
                componentProps: {
                  label: '',
                  id: projectData.id,
                  reportId: 'id',
                  source: 'title',
                  status: 'status',
                  externalReportLink: 'externalReportLink',
                  sx: { color: 'text.secondary' },
                },
                gridCellProps: { size: 5.5 },
              },
              {
                type: EDITABLE_LIST_FIELD,
                source: 'lastUpdate',
                isEditor: false,
                component: FormattedTextField,
                componentProps: {
                  label: '',
                  format: (v: string) => moment(v).format(DATE_FORMAT.API_ONLY_DATE),
                  sx: { color: 'text.secondary' },
                },
                gridCellProps: { size: 4 },
              },
              {
                type: EDITABLE_LIST_FIELD,
                isEditor: false,
                source: 'status',
                component: StatusField,
                gridCellProps: { size: 1.5, display: 'flex', justifyContent: 'end' },
              },
              {
                type: EDITABLE_LIST_CUSTOM,
                component: ModalAccessButton,
                componentProps: {
                  IconComponent: AppRegistrationIcon,
                  permission: SAVE_EXTERNAL_REPORT,
                  isVisible: (rowData: Data) => rowData.externalReportLink,
                  onClick: (rowData: Data) => {
                    setUpdateExternalModalData(rowData);
                    setUpdateExternalModalOpen(true);
                  },
                },
                gridCellProps: {
                  size: 0.5,
                  sx: { '.MuiFormControl-root': { mb: 0 }, display: 'flex', justifyContent: 'end' },
                },
              },
            ]);
          }}
        />
      </ProjectSectionAccordion>
      <Modal
        isOpen={modalOpen}
        onToggle={() => setModalOpen(false)}
        hasCloseIcon
        hasCancelButton={false}
        title="New customer report already exists"
      >
        <Typography>
          You already have draft report {draftReport?.title ? ` "${draftReport.title}"` : ''}. Please, update or cancel
          current report with draft status before creation the new one.
        </Typography>
      </Modal>
      <ExternalReportModal
        externalModalOpen={externalModalOpen}
        setExternalModalOpen={setExternalModalOpen}
        id={projectData.id}
      />
      <ExistingReportModal
        modalOpen={existingReportModalOpen}
        setModalOpen={setExistingReportModalOpen}
        inApprovalReport={inApprovalReport}
        rejectedReport={rejectedReport}
      />
      <UpdateExternalReportModal
        modalOpen={updateExternalModalOpen}
        setModalOpen={setUpdateExternalModalOpen}
        initialData={{
          projectId: projectData.id,
          index: updateExternalModalData?.index,
          id: updateExternalModalData?.id,
          periodStart: updateExternalModalData?.periodStart,
          periodEnd: updateExternalModalData?.periodEnd,
          externalReportLink: updateExternalModalData?.externalReportLink,
        }}
      />
    </>
  );
};

export default ReportList;
